import axios from "axios";

const BASE_URL = process.env.REACT_APP_API_URL || "/api/v1";
export const api = axios.create({ baseURL: BASE_URL });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});
api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401) { localStorage.removeItem("token"); window.location.href = "/login"; }
    return Promise.reject(err);
  }
);
export const authAPI = {
  login: (email: string, password: string) =>
    api.post("/auth/login", new URLSearchParams({ username: email, password })),
  register: (data: any) => api.post("/auth/register", data),
  me: () => api.get("/auth/me"),
  updateMe: (data: { nombre?: string; apellido?: string; email?: string }) => api.put("/auth/me", data),
  changePassword: (data: { password_actual: string; password_nuevo: string }) => api.put("/auth/me/password", data),
};
export const jugadoresAPI = {
  listar: (params?: any) => api.get("/jugadores", { params }),
  crear: (data: any) => api.post("/jugadores", data),
  perfil: (id: string) => api.get(`/jugadores/${id}/perfil`),
  stats: (id: string) => api.get(`/jugadores/${id}/stats`),
  historicoFisico: (id: string) => api.get(`/jugadores/${id}/fisico/historico`),
  tendenciaFisica: (id: string, variable: string) =>
    api.get(`/jugadores/${id}/fisico/tendencia`, { params: { variable } }),
  dossierPdf: (id: string) => api.get(`/jugadores/${id}/dossier-pdf`, { responseType: "blob" }),
  actualizar: (id: string, data: any) => api.put(`/jugadores/${id}`, data),
};
export const statsAPI = {
  registrar: (data: any) => api.post("/stats", data),
  actualizar: (id: string, data: any) => api.put(`/stats/${id}`, data),
};
export const medicionesAPI = {
  registrar: (data: any) => api.post("/mediciones", data),
  listar: (jugadorId: string) => api.get("/mediciones", { params: { jugador_id: jugadorId } }),
};
export const wellnessAPI = {
  registrar: (data: any) => api.post("/wellness", data),
  historial: (jugadorId: string) => api.get(`/wellness/${jugadorId}`),
  dashboardGrupal: (academiaId: string) => api.get(`/analytics/equipo/${academiaId}/wellness-dashboard`),
};
export const pagosAPI = {
  cartera: (academiaId: string) => api.get(`/pagos/cartera/${academiaId}`),
  registrar: (data: any) => api.post("/pagos", data),
  subirComprobante: (id: string, file: File) => {
    const form = new FormData(); form.append("file", file);
    return api.put(`/pagos/${id}/comprobante`, form);
  },
  enviarRecordatorios: (academiaId: string) => api.post(`/pagos/recordatorio/${academiaId}`),
  historial: (jugadorId: string) => api.get("/pagos", { params: { jugador_id: jugadorId } }),
};
export const analyticsAPI = {
  riesgoLesion: (jugadorId: string) => api.get(`/analytics/jugador/${jugadorId}/riesgo-lesion`),
  cargaRecomendada: (jugadorId: string) => api.get(`/analytics/jugador/${jugadorId}/carga-recomendada`),
  wellnessDashboard: (academiaId: string, categoriaId?: string) =>
    api.get(`/analytics/equipo/${academiaId}/wellness-dashboard`, { params: categoriaId ? { categoria_id: categoriaId } : {} }),
  wellnessDashboardCategoria: (categoriaId: string) =>
    api.get(`/analytics/categoria/${categoriaId}/wellness-dashboard`),
};
export const partidosAPI = {
  crear: (data: any) => api.post("/partidos", data),
  listar: (academiaId: string, categoriaId?: string, torneoId?: string) =>
    api.get("/partidos", { params: { academia_id: academiaId, ...(categoriaId && { categoria_id: categoriaId }), ...(torneoId && { torneo_id: torneoId }) } }),
  detalle: (id: string) => api.get(`/partidos/${id}`),
  actualizar: (id: string, data: any) => api.put(`/partidos/${id}`, data),
};
export const notasAPI = {
  crear: (data: any) => api.post("/notas", data),
  listar: (jugadorId: string) => api.get("/notas", { params: { jugador_id: jugadorId } }),
};
export const categoriasAPI = {
  listar: (academiaId?: string) => api.get("/categorias", { params: academiaId ? { academia_id: academiaId } : {} }),
  crear: (data: any) => api.post("/categorias", data),
  obtener: (id: string) => api.get(`/categorias/${id}`),
  actualizar: (id: string, data: any) => api.put(`/categorias/${id}`, data),
  jugadores: (id: string) => api.get(`/categorias/${id}/jugadores`),
  staff: (id: string) => api.get(`/categorias/${id}/staff`),
  torneos: (id: string) => api.get(`/categorias/${id}/torneos`),
  partidos: (id: string, torneoId?: string) =>
    api.get(`/categorias/${id}/partidos`, { params: torneoId ? { torneo_id: torneoId } : {} }),
  tablaPosiciones: (id: string) => api.get(`/categorias/${id}/tabla-posiciones`),
  asignarStaff: (data: any) => api.post("/categorias/staff-categoria", data),
  desasignarStaff: (asignacionId: string) => api.delete(`/categorias/staff-categoria/${asignacionId}`),
};
export const torneosAPI = {
  listar: (academiaId?: string) => api.get("/torneos", { params: academiaId ? { academia_id: academiaId } : {} }),
  crear: (data: any) => api.post("/torneos", data),
  obtener: (id: string) => api.get(`/torneos/${id}`),
  actualizar: (id: string, data: any) => api.put(`/torneos/${id}`, data),
  inscribirCategoria: (torneoId: string, data: any) => api.post(`/torneos/${torneoId}/inscribir-categoria`, data),
  tablaPosiciones: (torneoId: string, grupo?: string) =>
    api.get(`/torneos/${torneoId}/tabla-posiciones`, { params: grupo ? { grupo } : {} }),
  calendario: (torneoId: string, categoriaId?: string) =>
    api.get(`/torneos/${torneoId}/calendario`, { params: categoriaId ? { categoria_id: categoriaId } : {} }),
};

export const staffAPI = {
  listar: () => api.get("/staff"),
  crear: (data: any) => api.post("/staff", data),
  actualizar: (id: string, data: any) => api.put(`/staff/${id}`, data),
  desactivar: (id: string) => api.delete(`/staff/${id}`),
};

export const usuariosAPI = {
  listar: () => api.get("/usuarios"),
  crear: (data: any) => api.post("/usuarios", data),
  actualizar: (id: string, data: any) => api.put(`/usuarios/${id}`, data),
  desactivar: (id: string) => api.delete(`/usuarios/${id}`),
};

export const auditoriaAPI = {
  listar: (params?: {
    user_id?: string; user_email?: string; user_rol?: string;
    modulo?: string; metodo?: string; fecha_desde?: string; fecha_hasta?: string;
    status_code?: number; buscar?: string; limite?: number; offset?: number;
  }) => api.get("/auditoria", { params }),
  resumen: (dias?: number) => api.get("/auditoria/resumen", { params: dias ? { dias } : {} }),
  usuariosActivos: (dias?: number) => api.get("/auditoria/usuarios-activos", { params: dias ? { dias } : {} }),
};

export const wompiAPI = {
  conceptos:   () => api.get("/wompi/conceptos"),
  crear:       (data: { concepto: string; concepto_detalle?: string; valor: number; notas?: string }) =>
                 api.post("/wompi/crear", data),
  estado:      (referencia: string) => api.get(`/wompi/estado/${referencia}`),
  misPagos:    () => api.get("/wompi/mis-pagos"),
  todosAdmin:  (skip?: number, limit?: number) =>
                 api.get("/wompi/admin/todos", { params: { skip, limit } }),
};
